import React, { useState, useEffect } from 'react';
import { ButtonDropdown } from 'components/Base/ButtonDropdown';
import { TMenuProps } from '@idaho-aeyc/ui-kit';
import { Flex } from 'antd';
import { TTask } from 'data/types/pillarsActionPlan.types';
import { useUpdateTaskMutation } from 'redux/apiSlice/pillarsActionPlan.slice';
import { Tag, TagColors } from '@packages/utils';
import { StatusType } from '../../TacticTable.types';
import { ChevronDown } from '../../../../../../../assets/icon/icon';

type TaskStatusDropdownProps = {
  task: TTask;
  value: StatusType;
  disabled?: boolean;
};

const taskStatusColorMapper: Record<StatusType, TagColors> = {
  TODO: 'grey',
  IN_PROGRESS: 'primary',
  STALLED: 'danger',
  COMPLETED: 'success',
};

const TaskStatusDropdown = ({
  task,
  value,
  disabled,
}: TaskStatusDropdownProps) => {
  const [updateTask] = useUpdateTaskMutation();
  const [currentStatus, setCurrentStatus] = useState<StatusType>(value);

  // Update local state when prop changes
  useEffect(() => {
    setCurrentStatus(value);
  }, [value]);

  const statusLabels = {
    TODO: 'To Do',
    IN_PROGRESS: 'In Progress',
    STALLED: 'Stalled',
    COMPLETED: 'Completed',
  };

  const statusOptions: StatusType[] = [
    'TODO',
    'IN_PROGRESS',
    'STALLED',
    'COMPLETED',
  ];

  const handleMenuClick = (option: StatusType) => async () => {
    if (!task || option === currentStatus) return;

    setCurrentStatus(option);

    await updateTask({
      taskId: Number(task.taskId),
      field: 'STATUS',
      value: option,
    });
  };

  const items: TMenuProps['items'] = statusOptions.map(option => ({
    label: (
      <Tag
        color={taskStatusColorMapper[option]}
        data-cy="task-status-tag"
        style={{ width: 95, textAlign: 'center', marginRight: 0 }}
      >
        {statusLabels[option]}
      </Tag>
    ),
    key: option,
    onClick: handleMenuClick(option),
    disabled,
  }));

  return (
    <Flex gap={8} align="center" data-cy="task-status-dropdown">
      <div>
        <Tag
          color={taskStatusColorMapper[currentStatus]}
          data-cy="task-status-tag"
          style={{ width: 95, textAlign: 'center', marginRight: 0 }}
        >
          {statusLabels[currentStatus]}
        </Tag>
      </div>
      <ButtonDropdown
        buttonProps={{
          children: <ChevronDown size={16} />,
          color: 'primary',
          style: {
            border: '0',
            background: 'none',
            padding: 0,
          },
        }}
        dropdownProps={{
          menu: {
            items,
          },
          rootClassName: 'button-dropdown',
          placement: 'bottomRight',
          disabled,
        }}
      />
    </Flex>
  );
};

export default TaskStatusDropdown;
